export const AMPLIFY_LOCAL_STORAGE_PREFIX = 'CognitoIdentityServiceProvider.';

// currentSession() rebuilds from LastAuthUser and token keys in storage.
const PRESERVED_AMPLIFY_KEY_SUFFIXES = [
  '.LastAuthUser',
  '.idToken',
  '.accessToken',
  '.refreshToken',
  '.clockDrift',
];

const isPreservedAmplifyKey = (key: string): boolean =>
  PRESERVED_AMPLIFY_KEY_SUFFIXES.some((suffix) => key.endsWith(suffix));

export const WORKSPACE_LOGIN_SESSION_ID = 'workspace_login';

export const LOGIN_RECOVERY_STAGES = [
  'as_is',
  'clear_amplify',
  'clear_portal_cookies',
] as const;

export type LoginRecoveryStage = (typeof LOGIN_RECOVERY_STAGES)[number];

export const LOGIN_RECOVERY_MESSAGE = 'login recovery';

type KeyedStorage = Pick<Storage, 'removeItem'> & Record<string, unknown>;

const PORTAL_NEXT_PREFIX = '/portal/';

export const resolveFailingPortalId = (
  search: string = typeof window === 'undefined' ? '' : window.location.search,
): string => {
  try {
    const next = new URLSearchParams(search).get('next');
    if (next?.startsWith(PORTAL_NEXT_PREFIX)) {
      const [path] = next.split('?');
      const [portalId] = path.replace(PORTAL_NEXT_PREFIX, '').split('/');
      if (portalId) {
        return portalId;
      }
    }
  } catch {
    // Fall through to the login-flow session. Never block retry.
  }
  return WORKSPACE_LOGIN_SESSION_ID;
};

export const getPreservedLoginSearch = (
  search: string = typeof window === 'undefined' ? '' : window.location.search,
): string => search;

export const clearAmplifyLocalStorageKeys = (
  storage: KeyedStorage = window.localStorage,
): string[] => {
  const removed: string[] = [];
  try {
    const keys = Object.keys(storage);
    keys.forEach((key) => {
      if (
        key.startsWith(AMPLIFY_LOCAL_STORAGE_PREFIX) &&
        !isPreservedAmplifyKey(key)
      ) {
        storage.removeItem(key);
        removed.push(key);
      }
    });
  } catch {
    // Clearing Amplify state must never block the retry itself.
  }
  return removed;
};

export const reportLoginRecovery = (fields: {
  email?: string;
  recoveryStage: LoginRecoveryStage;
}) => {
  console.info(
    JSON.stringify({
      message: LOGIN_RECOVERY_MESSAGE,
      recoveryStage: fields.recoveryStage,
      email: fields.email?.toLowerCase(),
    }),
  );
};

export const runStagedWorkspaceRetry = async (input: {
  retry: () => Promise<boolean>;
  clearPortalCookies: (portalId: string) => Promise<void>;
  email?: string;
  portalId: string;
  storage?: KeyedStorage;
}): Promise<LoginRecoveryStage | undefined> => {
  if (await input.retry()) {
    reportLoginRecovery({
      email: input.email,
      recoveryStage: 'as_is',
    });
    return 'as_is';
  }

  clearAmplifyLocalStorageKeys(input.storage ?? window.localStorage);
  if (await input.retry()) {
    reportLoginRecovery({
      email: input.email,
      recoveryStage: 'clear_amplify',
    });
    return 'clear_amplify';
  }

  // workspace_login is the pre-portal session loadWorkspaces reads. Clearing
  // it on a bare /login URL removes the only auth source for the retry.
  if (input.portalId !== WORKSPACE_LOGIN_SESSION_ID) {
    try {
      await input.clearPortalCookies(input.portalId);
    } catch {
      // Cookie clear is best-effort; still try the workspaces fetch again.
    }
    if (await input.retry()) {
      reportLoginRecovery({
        email: input.email,
        recoveryStage: 'clear_portal_cookies',
      });
      return 'clear_portal_cookies';
    }
  }

  return undefined;
};
